import { useEffect } from "react";
import { Link } from "react-router-dom";
import SEO from "../../components/SEO";

// All images sourced from fostercareanthemproject.com + the emergent customer-assets for album art
const GALLERY = [
  "https://fostercareanthemproject.com/gallery/awareness/if-we-dont-1.jpg",
  "https://fostercareanthemproject.com/gallery/philly-foster-kid/01.jpg",
  "https://fostercareanthemproject.com/gallery/philly-foster-kid/04.jpg",
  "https://fostercareanthemproject.com/gallery/awareness/if-we-dont-2.jpg",
  "https://fostercareanthemproject.com/gallery/philly-foster-kid/07.jpg",
  "https://fostercareanthemproject.com/gallery/image-large/01.jpg",
  "https://fostercareanthemproject.com/gallery/philly-foster-kid/11.jpg",
  "https://fostercareanthemproject.com/gallery/awareness/if-we-dont-3.jpg",
  "https://fostercareanthemproject.com/gallery/philly-foster-kid/14.jpg",
  "https://fostercareanthemproject.com/gallery/image-large/03.jpg",
  "https://fostercareanthemproject.com/gallery/awareness/if-we-dont-4.jpg",
  "https://fostercareanthemproject.com/gallery/philly-foster-kid/17.jpg",
];

const PILLARS = [
  { n: "I.", t: "Music as a Vessel", d: "Each song is engineered to carry weight — testimony, lament, defiance, prayer. Blending faith, social conscience, and lived experience into emotionally resonant tools for digital storytelling." },
  { n: "II.", t: "Story as a Weapon", d: "Statistics are forgotten. Stories aren't. The catalog amplifies the lived experiences of children in care and humanizes system-level conversations the news cycle overlooks." },
  { n: "III.", t: "Redemption as the Goal", d: "Created in proximity to frontline foster care work, this music exists to serve the mission — not the marketplace. Designed to support organizations engaged in recruitment, reform, and family support." },
];

export default function Mission() {
  useEffect(() => { window.scrollTo(0, 0); }, []);
  return (
    <div data-testid="anthem-mission-page">
      <SEO
        path="/anthem/mission"
        title="The Mission | Foster Care Anthem Project"
        description="Foster Care Anthem is a faith-rooted, availability-based music initiative supporting foster care awareness, recruitment, reform, and family support. Music as a vessel · Story as a weapon · Redemption as the goal."
      />
      <section className="border-b border-an-border">
        <div className="container-xl pt-24 pb-16">
          <div className="overline text-an-gold mb-5">The Mission</div>
          <h1 className="font-display text-6xl md:text-8xl leading-[0.9]">Darkness <br/><span className="italic">To Light</span></h1>
          <p className="mt-8 text-an-muted text-lg max-w-3xl leading-relaxed">
            Foster Care Anthem is an independent, faith-rooted music initiative developed to support awareness, education, and advocacy surrounding foster care. VOL 1 — released January 30, 2026 — is a 15-track collection created to serve as a storytelling and awareness resource for advocates, organizations, faith leaders, and communities working within the foster care system.
          </p>
        </div>
      </section>

      <section className="border-b border-an-border">
        <div className="container-xl py-20 grid md:grid-cols-3 gap-0 border border-an-border">
          {PILLARS.map((p, i) => (
            <div key={i} className={`p-10 ${i<2 ? "md:border-r" : ""} border-an-border`}>
              <div className="font-mono text-an-gold text-lg mb-4">{p.n}</div>
              <h3 className="font-display text-2xl mb-3">{p.t}</h3>
              <p className="text-sm text-an-muted leading-relaxed">{p.d}</p>
            </div>
          ))}
        </div>
      </section>

      <section className="border-b border-an-border">
        <div className="container-xl py-24">
          <div className="overline text-an-gold mb-4">Faces of the Mission · If we don't do it who will?</div>
          <h2 className="font-display text-4xl md:text-5xl leading-tight max-w-4xl">
            Every frame is a child <span className="italic">someone forgot to fight for.</span>
          </h2>
          <div className="mt-12 grid grid-cols-2 md:grid-cols-4 gap-4">
            {GALLERY.map((img, i) => (
              <div key={i} className="aspect-[3/4] overflow-hidden border border-an-border bg-an-surface">
                <img src={img} alt="" loading="lazy" className="w-full h-full object-cover grayscale-[0.15] hover:grayscale-0 transition-all duration-700" />
              </div>
            ))}
          </div>
          <p className="mt-10 text-an-muted max-w-3xl">
            These aren't stock images. Every face, every silhouette, every street corner you see was rendered to honor a real child still living inside the foster system today.
          </p>
        </div>
      </section>

      <section>
        <div className="container-xl py-24 max-w-4xl">
          <blockquote className="font-display text-3xl md:text-4xl italic leading-tight">
            "Foster Care Anthem — VOL 1 is a 15-track awareness album designed to amplify the voices of children and families impacted by foster care. Through music, storytelling, and advocacy, this project turns attention into action."
          </blockquote>
          <div className="overline text-an-muted mt-6">— Foster Care Anthem Project</div>
          <div className="mt-12">
            <h3 className="font-display text-2xl mb-5">Hear the catalog.</h3>
            <Link to="/anthem/catalog" className="btn-primary" data-testid="mission-catalog-link">Open The Catalog</Link>
          </div>
        </div>
      </section>
    </div>
  );
}
